/* eslint-disable @next/next/no-img-element */
/* eslint-disable indent */
/**
 * Step Five: collect credit card info via Stripe Elements components and sync to Starbase
 */
import React, { useState, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import { useFeatureValue } from '@growthbook/growthbook-react'
import { mq, storageMode, googleAnalyticsEvent, useBooleanState, getArrivalWindow, getAppointmentEndTime, PLATFORM_PAY, notifyBugsnag, StepNotReadyError } from '../../../helpers'
import _ from 'lodash'
import { flow, map, sum, omit } from 'lodash/fp'
import {
  useMe,
  useAllMarketServices,
  claimUserCredit,
  calcApptPrice,
  bookAppointment // platform-pay books directly (no server-side 3DS handshake)
} from '../../../services/GraphQlClientService'
import { useThreeDSecureBooking } from '../../../helpers/useThreeDSecureBooking'
import { attemptBookingHeal, mergeFreshAvailability } from '../../../helpers/bookingHeal'
import { checkoutPaymentContext, trackPromoCodeResult } from '../../../helpers/tracking'
import Color from 'color'
import { gsColors } from '../../../styles/Variables'
import Button from './components/Button'
import Input from './components/Input'
import RegularInput from '../../Input'
import Label from './components/Label'
import checkmarkSvg from '../../../../public/images/checkmark.svg'
import LoadingStep from './components/LoadingStep'
import {
  formatDateLong,
  formatTimeShort,
  formatCurrency,
  serviceAndAddOnServiceInfo,
  trackCheckoutStarted,
} from '../../../helpers'

import { Section, Header2, Text } from '../../../styles/Components'
import PaymentCard from '../../checkout/PaymentCard'
import DynamicGratuity from '../../checkout/DynamicGratuity'
import FullyDynamicGratuity from '../../checkout/FullyDynamicGratuity'
import ProTrustBanner from './components/ProTrustBanner'
import SummaryPolicies from './components/SummaryPolicies'
import CalendarDropdown from '../../CalendarDropdown'


const SummarySection = styled(Section)`
  background-color: ${gsColors.khaki};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 37px 0 0;
  width: 100%;
  max-width: 624px;

  ${mq('max', '768px')} {
    max-width: none;
    margin: 0 0 30px;
  }

  ${mq('min', '768px')} {
    padding-top: 50px;
  }

  .summary-content {
    width: 100%;
    max-width: 450px;
    
    ${mq('max', '768px')} {
      max-width: none;
      padding: 0 16px;
    }

    .date-time {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      margin-bottom: 30px;

      img {
        width: 30px;
        height: 30px;
        object-fit: contain;
        flex-shrink: 0;
        margin-right: 18px;

        ${mq('max', '768px')} {
          width: 24px;
          height: 24px;
        }
      }

      .summary-detail {
        display: flex;
        flex-direction: column;
        

        p {
          ${mq('max', '768px')} {
            font-size: 16px;
          }
        }
      }
    }

    .arrival-window-text {
      margin: 0 0 30px;
      font-family: Euclid;

      ${mq('max', '768px')} {
        max-width: 100%;
      }

      b {
        font-weight: 600 !important;
      }
    }

    .date-time-content {
      display: flex;
      flex-direction: column;
    }
  }

  .summary-divider {
    width: 100%;
    border-bottom: 1px solid ${gsColors.textGrey};
    margin-bottom: 38px;
  }

  .address {
    margin-bottom: 30px;

    img {
      width: 15px;
      height: 19px;
    }
  }

  .info-icon-container {
    display: inline-block;    
  }

  .prices {
    line-height: 100%;
    font-family: Euclid;

    li {
      color: ${gsColors.meredithGrey};
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin: 12px 0;
      font-size: 16px;

      span:first-of-type {
        font-weight: 500;
      }

      span:last-of-type {
        font-weight: 600;
      }

      &.service,
      &.total {
        color: ${gsColors.black};
      }

      &.service {
        margin: 18px 0;
        font-size: 18px;

        span {
          font-weight: 400;
        }

        ${mq('max', '768px')} {
          font-size: 16px;
        }

        .name {
          text-transform: capitalize;
        }
      }

      &.credit {
        color: ${gsColors.cobalt};
      }

      &.subtotal {
        margin-top: 20px;
      }

      &.total {
        font-size: 20px;

        span {
          font-weight: 600;
        }
      }

      &.promo-code {
        display: block;
        margin: 30px 0;
        font-family: Euclid !important;
        font-size: 18px !important;

        /* On mobile the promo entry moves out of the summary and sits between the
           payment options and gratuity (rendered by MobilePromoCode). */
        ${mq('max', '768px')} {
          display: none;
        }

        ${mq('min', '768px')} {
          margin-top: 20px;
        }
        
        label {
          color: ${gsColors.black};
          font-family: Euclid !important;
          font-size: 18px;
        }

        input, button {
          font-size: 16px;
        }
      }
    }
  }

  .input-group-btn {
    position: relative;

    input {
      padding-right: 56px;

      ${mq('min', '768px')} {
        font-size: 14px;
      }
    }
  }

  .promo-code-message {
    font-size: 13px;
    margin-top: 10px;
    text-align: center;

    &.success {
      color: ${gsColors.persianGreen};
    }

    &.error {
      color: ${gsColors.errorRed};
    }
  }
`

const StepFiveLayout = styled.div`
  width: 100%;
`

const SummaryTitle = styled(Header2)`
  font-family: Euclid;
  font-weight: 600;

  ${mq('max', '768px')} {
    font-size: 22px;
  }
`

const StepTitle = styled(Header2)`
  max-width: 772px;
  margin: 0 auto 40px;

  ${mq('max', '768px')} {
    max-width: none;
    margin: 24px 24px 32px;
  }
`

const Columns = styled.div`
  width: 100%;

  ${mq('min', '768px')} {
    display: flex;
    justify-content: center;
    align-items: flex-start;
  }
`

const LeftColumn = styled.div`
  max-width: 600px;

  section {
    &:first-of-type {
      // margin: 0 16px 30px;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-bottom: 0 !important;
      
      ${mq('max', '768px')} {
        margin: 0 0 30px;
        padding: 30px 16px 18px;

        h2 {
          width: 100%;
        }
      }
      
      div {
        width: 100%;
        max-width: 379px;

        ${mq('max', '768px')} {
          max-width: none;
        }
      }
    }
    .protection {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 10px;
      
      p {
        color: #878787;
        font-size: 12px;
      }

      img {
        max-height: 22px;

      }
    }

  }
`

const WeddingDetailsSection = styled(Section)`
  max-width: 600px;
  padding: 50px 60px;

  ${mq('max', '768px')} {
    margin: 0 0 30px;
    max-width: none;
    padding: 50px 16px;
  }

  div {
    &:first-of-type {
      margin-bottom: 30px;

      ${mq('max', '768px')} {
        max-width: none;
      }
    }

    &:nth-of-type(2) {
      max-width: 379px;

      ${mq('max', '768px')} {
        max-width: none;
      }
    }
  }



  .gs-checkbox-circle {

    input:checked + label {
      .box {
        background-color: ${gsColors.sunsetOrange};
        background-image: url(${checkmarkSvg});
        box-shadow: none;
      }
    }

    input[type='checkbox'] {
      display: none;
    }

    .box {
      cursor: pointer;
      background: ${gsColors.hintOfRed} no-repeat center;
      box-shadow: inset 0 0 2px 0 ${Color(gsColors.black).alpha(0.2).string()};
      border-radius: 50%;
      display: inline-block;
      position: relative;
      transition: all 250ms;
      width: 24px;
      height: 24px;
      margin-right: 10px;
    }
    
    label {
      cursor: pointer;
      margin-top: 20px;
      content-align: center;
      display: flex;
    }
  }

  .input-group-btn {
    margin-top: 10px;
  }

  .wedding-date-input-group {
    border: none;
    border-bottom: 1px solid black;
    display: flex;
    align-items: center;
    padding-left: 5px;
  }

  .react-datepicker__input-container input {
    border: none;
    font-family: Brown, sans-serif;
    font-size: 16px;
    line-height: 20px;
    padding: 10px 0;
    width: 100%;
    margin-left: 10px;
  }


  .gs-checkbox-circle {
    margin-top: 10px;
  }
`

const PromoButton = styled(Button)`
  background-color: transparent;
  color: ${gsColors.sunsetOrange};
  font-size: 13px;
  padding: 0;
  position: absolute;
  top: 0;
  right: 0;
  transition: color 250ms;
  width: auto;
  height: 40px;

  &[disabled] {
    background-color: transparent;
    color: ${gsColors.venus};
  }
`

/**
 * Mobile-only promo entry. On mobile the Cro Sprint layout places "Add a promo code"
 * between the payment options and the gratuity selector; on desktop the promo lives in
 * the summary breakdown instead (this wrapper is hidden there).
 */
const MobilePromoCode = styled.div`
  display: none;

  ${mq('max', '768px')} {
    display: block;
    margin: 24px 0;
    padding: 0 16px;
    font-family: Euclid;
    font-size: 18px;
  }

  .input-group-btn {
    position: relative;

    input {
      padding-right: 56px;
    }
  }

  .promo-code-message {
    font-size: 13px;
    margin-top: 10px;
    text-align: center;

    &.success {
      color: ${gsColors.persianGreen};
    }

    &.error {
      color: ${gsColors.errorRed};
    }
  }
`

const Five = ({
  state: {
    address,
    market: { timezone, adjustableTipRate, fullyAdjustableTipRate, shortName: marketShortName, cancellationPolicy },
    selectedAppointment: appointment,
    selectedPaymentMethodId,
    paymentMethods,
    bookingError,
    step,
    isDesktop,
    weddingDetails: {
      weddingDate,
      weddingLocation,
      weddingOtherServices = []
    } = {},
    serviceRequests,
    platformPayAvailability,
  },
  dispatch,
  setIsStepValid,
  code
}) => {
  if (typeof weddingDate === 'string') {
    dispatch({ weddingDetails: { weddingDate: new Date(weddingDate) } })
  }

  const team = _.chain(appointment?.appointmentServices || [])
    .map('providerId')
    .uniq()
    .value()
    .length || 1

  // promo code state variables
  const [promoCode, setPromoCode] = useState('') // used for actual promo code input field
  const [appliedPromoCode, setAppliedPromoCode] = useState('') // used to send promo code to tracking
  const [appliedPromoCodeAmt, setAppliedPromoCodeAmt] = useState(0)
  const [promoCodeClass, setPromoCodeClass] = useState('')
  const [promoCodeMessage, setPromoCodeMessage] = useState('')
  const [weddingCalendarOpen, toggleWeddingCalendar] = useBooleanState()
  const [tipRate, setTipRate] = useState(0.2) // Track tip rate (default 20%)
  const [willTipInCash, setWillTipInCash] = useState(false)
  const [isCustomTipInputOpen, setIsCustomTipInputOpen] = useState(false)
  // `hide-tip-in-cash` experiment: treatment hides the "I'll tip in cash" checkbox
  // at checkout. Default false = control = cash option shown, so an unloaded flag is safe.
  const showTipInCash = !useFeatureValue('hide-tip-in-cash', false)

  const bookWith3DS = useThreeDSecureBooking()

  useEffect(() => {
    if (bookingError) {
      dispatch({ bookingError: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const storage = storageMode('localStorage')
  // controls whether to display the menu to select an existing credit card
  const [hasExistingPaymentMethods, setHasExistingPaymentMethods] = useState(
    paymentMethods && paymentMethods.length
  )

  // apply promo code
  // silent: when true (auto-added codes from the URL), failures are swallowed so
  // no error banner shows; a successful claim still displays its credit message.
  const applyPromoCode = async (promoCode, { silent = false } = {}) => {
    const promoContext = {
      source: silent ? 'url' : 'manual',
      placement: silent ? 'url' : window.matchMedia('(max-width: 768px)').matches ? 'mobile_payment' : 'desktop_summary',
      market: marketShortName,
    }
    try {
      // claim
      const userCredits = await claimUserCredit(me.id, promoCode)

      // an array of credits is returned so sum up amounts
      const amount = userCredits[0].isFraction
        ? userCredits[0].amount * 100
        : flow(map('amount'), sum)(userCredits)

      trackPromoCodeResult(true, {
        ...promoContext,
        discountAmount: amount,
        discountType: userCredits[0].isFraction ? 'percent' : 'fixed',
      })

      // display amount applied
      setPromoCodeMessage(
        `${userCredits[0].isFraction ? `${amount}%` : formatCurrency(amount)
        } in credit has been applied to your account.`
      )
      setPromoCodeClass('success')
      setAppliedPromoCode(promoCode) // used to track what promo code used with rakuten
      setAppliedPromoCodeAmt(amount) // used to track how much the promo was with rakuten
      setPromoCode('') // clear input

      // recalculate appointment price w/ new promo credits applied
      updateAppointmentPrice()
    } catch (error) {
      trackPromoCodeResult(false, {
        ...promoContext,
        failureCategory: error.graphQLErrors?.[0]?.type || error.errors?.[0]?.type || 'unknown',
      })
      if (silent) {
        return
      }

      // display any error including invalid/expired code
      setPromoCodeMessage(error.message)
      setPromoCodeClass('error')
      setPromoCode('')
    }
  }

  // load market services for service info
  const allMarketServices = useAllMarketServices(appointment?.address?.market?.id)

  const marketServicesForMarket = _(allMarketServices)
    .filter(ms => ms.marketId === appointment.address.market?.id)
    .value()

  // load user info for payment methods
  const me = useMe()

  const dayViewEnabled = Boolean(appointment.bookingTokens?.length)
  const priceRequestRef = useRef(0)
  const checkoutTrackedRef = useRef(false)
  // track checkout started once, after everything the event needs has loaded: the market services and
  // the checkout-computed price (day-view slots arrive with finalPriceWithTaxAndTip null)
  useEffect(() => {
    if (checkoutTrackedRef.current) {
      return
    }
    if (!marketServicesForMarket.length || appointment.finalPriceWithTaxAndTip == null) {
      return
    }
    checkoutTrackedRef.current = true
    trackCheckoutStarted(appointment, marketServicesForMarket, serviceRequests, { fromBookedServices: dayViewEnabled })
  }, [marketServicesForMarket, appointment, serviceRequests, dayViewEnabled])


  // applies a promo code that was saved from app reducer
  useEffect(() => {
    if (!code || !me) {
      return
    }

    applyPromoCode(code, { silent: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, me])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Platform pay is only a valid choice when there's a real amount to charge AND a device
  // wallet is present. The footer already mounts the wallet button only when
  // finalPriceWithTaxAndTip > 0; deriving eligibility here (and reusing it below) keeps the
  // selection, self-heal, and dropdown display in agreement with that gate. Without the total
  // check, a $0 total (e.g. fully credit-covered) with stale/persisted availability could
  // auto-select PLATFORM_PAY, hide the wallet button, and submit the card path with no
  // paymentMethodId. `total` is null while the price loads (see the refetch below), so
  // Number.isFinite gates that loading window out of eligibility.
  const total = appointment.finalPriceWithTaxAndTip
  const platformPayEligible =
    Number.isFinite(total) &&
    total > 0 &&
    !!(platformPayAvailability?.applePay || platformPayAvailability?.googlePay)

  // The card-less default-to-platform-pay is owned by InlinePaymentSelector (checkout/), the
  // only place that toggle renders (card-less + wallet available + chargeable total — the
  // same conditions this default targeted). Keeping it there lets a user's "Credit card"
  // choice (which clears the selection to null) stick instead of being snapped back to
  // PLATFORM_PAY by an effect here on every null selection.

  // Recover from a stale or now-ineligible PLATFORM_PAY selection. selectedPaymentMethodId +
  // platformPayAvailability both persist in the 'book' blob, so a wallet chosen on a prior
  // (wallet-capable, chargeable) session can be restored here where platform pay is no longer
  // valid — either no wallet on this device, OR a $0/non-positive total (e.g. fully
  // credit-covered). In those cases the footer hides "Book Now" and/or the card path would
  // submit with no paymentMethodId, leaving a broken/invalid state. Fixing the selection is
  // the root-cause fix: the footer button and dropdown both derive from selectedPaymentMethodId.
  //
  // Two "wait" guards so a VALID selection is never cleared prematurely: skip while the total
  // is still loading (null), and — at a chargeable total — skip until the ECE has reported
  // wallet availability (platformPayAvailability is undefined until onReady). Once determined,
  // if it's not eligible, fall back to the first saved card, or null (which reveals the
  // add-card form via PaymentCard's non-dropdown NewPaymentMethod branch). Mutually exclusive
  // from the default-select effect above (that needs eligible + no selection; this needs
  // PLATFORM_PAY selected + not eligible), so they cannot fight or loop.
  useEffect(() => {
    if (selectedPaymentMethodId !== PLATFORM_PAY) { return }
    if (!Number.isFinite(total)) { return } // total still loading — don't clear a valid selection
    if (!platformPayAvailability && total > 0) { return } // chargeable, but wallet not yet reported — wait for onReady
    if (platformPayEligible) { return } // still valid — keep it
    dispatch({ selectedPaymentMethodId: paymentMethods?.[0]?.id ?? null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentMethodId, platformPayAvailability, total, platformPayEligible, paymentMethods])

  /** Recalculates pricing and applies only the latest tip/promo request. */
  const updateAppointmentPrice = () => {
    const requestId = ++priceRequestRef.current
    const date = DateTime.fromSeconds(appointment.actualStartDateTime, { zone: 'utc' }).setZone(timezone).toISO()
    const addressOmit = _.omit(address, ['id', 'latitude', 'longitude', 'formatted', '__typename'])

    if (!addressOmit.pets?.length) {
      addressOmit.pets = 'None'
    }

    // these services carry server-owned fields (price, duration, customPrice) that
    // AppointmentServiceInput doesn't define; calcApptPrice filters them at the wire,
    // so they're passed through as-is here
    calcApptPrice(date, addressOmit, appointment.appointmentServices, tipRate).then(
      ({
        creditsApplied,
        totalPrice,
        taxAmount,
        tipAmount,
        handlingFee,
        finalPriceWithTaxAndTip,
        additionalFees
      }) => {
        if (requestId !== priceRequestRef.current) {
          return
        }
        dispatch({
          selectedAppointment: {
            taxAmount,
            tipAmount,
            handlingFee,
            finalPriceWithTaxAndTip,
            totalPrice,
            creditsApplied,
            additionalFees
          }
        })
      }
    ).catch(error => {
      // a failed recalculation leaves the previous price in place; report it
      // instead of letting it surface as an unhandled rejection
      notifyBugsnag(error, { step: 5, tipRate }, 'calcApptPrice')
    })
  }

  /**
   * Dynamic Tip Rate Integration
   *
   * Automatically recalculates appointment pricing when tip rate changes.
   * This enables real-time price updates when user toggles gratuity.
   */
  useEffect(() => {
    updateAppointmentPrice()
    return () => {
      // Ignore older tip/promo responses and any response after unmount.
      priceRequestRef.current += 1
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipRate])

  // Keep Book Now disabled while this step cannot book yet: the profile is
  // reloading (useMe resets to undefined whenever the access token changes) or
  // the payment methods have not been initialised. isStepValid persists in the
  // booking reducer, so without this the footer would keep the previous render's
  // enabled button while this render registers no handler, and a press would
  // throw. The placeholder handler guards the gap between this render and the
  // reducer update. No wallet callback is passed: the page keeps the one
  // registered by the previous render so an in-flight wallet confirm still books.
  const bookingNotReady = () => {
    throw new StepNotReadyError()
  }
  if (!me || !paymentMethods) {
    setIsStepValid(false, 'Book Now', bookingNotReady)
  }

  if (!me) {
    return <LoadingStep id="loading" />
  }
  //Store me as user info in local storage so the user remains logged in after booking flow

  storage.setItem(
    'userInfo',
    JSON.stringify(omit(['addresses', 'paymentMethods'], me))
  )


  // init existing payment methods and select one
  if (!paymentMethods) {
    if (
      me.paymentMethods &&
      me.paymentMethods.length &&
      !hasExistingPaymentMethods
    ) {
      // if we've just initialized payment methods update the state of existing payments methods
      setHasExistingPaymentMethods(true)
    }

    return dispatch({
      paymentMethods: me.paymentMethods,
      selectedPaymentMethodId: me.paymentMethods.length
        ? me.paymentMethods[0].id
        : null
    })
  }

  // Treat user as a new user in tracking if they haven't booked in over a year.
  // Hoisted out of the card callback so BOTH the card path and the platform-pay path
  // report new-user booking analytics — a wallet booking is still a booking.
  const daysSinceLastBooked = Math.abs(DateTime.fromISO(me.lastAppointmentBookedAt).diffNow('days').days)
  const isNewUser = !me.lastAppointmentBookedAt || daysSinceLastBooked >= 365

  // A fully credit-covered appointment ($0 total — the SAME signal that hides the tip selector,
  // FullyDynamicGratuity's `finalPrice === 0`) needs no payment method. So a brand-new user with
  // no saved card (e.g. redeemed a gift card) can book without being forced to add one: the card
  // input is hidden and the step is valid without a selectedPaymentMethodId. Backend accepts a $0
  // booking with no paymentMethodId. Guarded on 0 saved cards so a user who DOES have a card still
  // sees and can pick it. `=== 0` (not `<= 0`) keeps this in lockstep with the tip-selector gate.
  const noPaymentNeeded =
    !paymentMethods.length && appointment.finalPriceWithTaxAndTip === 0

  // Address payload shared by both booking paths. Mirrors the card path's original inline
  // omit(): strips server/geometry fields and conditionally removes `pets` when there are none.
  const addressOmit = omit(
    [
      '__typename',
      'formatted',
      'latitude',
      'longitude',
      ...(!address?.pets || !address?.pets.length ? ['pets'] : []),
    ],
    address,
  )

  // Assemble the appointment with wedding notes + tip fields, shared by the card and wallet paths.
  // The wedding-notes template literal below is copied verbatim from the original card callback so
  // the user-visible whitespace and newlines in the appointment notes stay byte-for-byte identical.
  // Unlike the original (which mutated appointment.notes in place), this copies first — the booking
  // payload is identical, but we avoid mutating the shared state object.
  const buildAppointmentForBooking = () => {
    let appt = { ...appointment }
    if (includesWeddingServices) {
      appt.notes =
        (appt.notes || '') +
        `
  Wedding Details:
    Date: ${weddingDate
          ? DateTime.fromJSDate(weddingDate).toLocaleString(DateTime.DATE_SHORT)
          : 'n/a'
        }. 
    Location: ${weddingLocation ? weddingLocation : 'n/a'}. 
    Other Services: ${weddingOtherServices.length ? weddingOtherServices.join(', ') : 'none'
        }. 
  `
    }
    return { ...appt, tipRate, willTipInCash }
  }

  // Shared success dispatch — advances to the confirmation step. Identical shape to the
  // original card-path dispatch so the confirmation step receives the same fields.
  const finalizeBooking = bookedAppointment =>
    dispatch({
      bookedAppointment: {
        ...bookedAppointment,
        dayViewBooking: dayViewEnabled,
        checkoutPaymentContext: checkoutPaymentContext(noPaymentNeeded ? null : selectedPaymentMethodId, platformPayAvailability),
      },
      isNewUser,
      appliedPromoCode,
      appliedPromoCodeAmt,
      marketServicesForMarket,
      serviceRequests,
      step: step + 1
    })

  // Context shared by both booking paths for healing searchExpired / providerBooked
  // failures (see helpers/bookingHeal.js): re-search availability with Step Three's
  // parameters, then either silently rebook the same slot with fresh booking tokens
  // or return the customer to the search step with an explanatory message.
  const bookingHealContext = {
    dispatch,
    appointment,
    address,
    serviceRequests,
    timezone,
    marketServices: marketServicesForMarket,
    market: { shortName: marketShortName },
    selectedPaymentMethodId,
  }

  // Completes a platform-pay (Apple/Google Pay) booking after the wallet has authorized the
  // PaymentIntent. Books directly via bookAppointment (NOT bookWith3DS): there is no
  // paymentMethodId and no supports3DSecure, so the server skips the 3DS handshake — the
  // wallet already satisfied SCA. Fires the same new-user GA event as the card path for parity.
  const platformPayConfirm = async paymentIntentId => {
    if (isNewUser) {
      googleAnalyticsEvent('book', { label: 'New User' })
    }
    const appt = {
      ...buildAppointmentForBooking(),
      paymentIntentId,
      paymentMethodId: undefined
    }
    let bookedAppointment
    try {
      bookedAppointment = await bookAppointment(appt, addressOmit)
    } catch (err) {
      // The wallet already authorized an uncaptured PaymentIntent, so the heal rebooks
      // with the SAME paymentIntentId (carried on appt) — never a second authorization.
      // If the slot is gone the customer returns to search and the uncaptured hold
      // expires on its own. Non-healable errors rethrow to PlatformPayButton's catch,
      // which routes them to the footer's onError telemetry as before.
      bookedAppointment = await attemptBookingHeal(err, {
        ...bookingHealContext,
        rebook: fresh => bookAppointment(mergeFreshAvailability(appt, fresh), addressOmit),
      })
      if (!bookedAppointment) {
        return // healed by returning the customer to the search step
      }
    }
    finalizeBooking(bookedAppointment)
  }

  // step is valid if we have a payment method AND the user isn't mid-custom-tip-entry
  setIsStepValid(
    (!!selectedPaymentMethodId || noPaymentNeeded) && !isCustomTipInputOpen,
    'Book Now',
    async () => {
      if (isNewUser) {
        googleAnalyticsEvent('book', { label: 'New User' })
      }

      const appt = { ...buildAppointmentForBooking() }
      // Guard against the PLATFORM_PAY sentinel: it is not a real Stripe payment method id, so
      // never send it as paymentMethodId. Wallet bookings go through platformPayConfirm instead.
      if (selectedPaymentMethodId && selectedPaymentMethodId !== PLATFORM_PAY) {
        appt.paymentMethodId = selectedPaymentMethodId
      }

      let bookedAppointment
      try {
        bookedAppointment = await bookWith3DS(appt, addressOmit, marketShortName)
      } catch (err) {
        // Rebooking through bookWith3DS keeps the fresh attempt on the full card
        // rails (the bank may challenge it again). Non-healable errors rethrow to
        // the footer's submit catch, which shows the error line as before.
        bookedAppointment = await attemptBookingHeal(err, {
          ...bookingHealContext,
          rebook: fresh =>
            bookWith3DS(mergeFreshAvailability(appt, fresh), addressOmit, marketShortName),
        })
        if (!bookedAppointment) {
          return // healed by returning the customer to the search step
        }
      }
      finalizeBooking(bookedAppointment)
    },
    platformPayConfirm, // 4th arg — wallet booking path, read lazily by the footer at confirm time
  )

  // fetch service names, prices for summary
  const appointmentServiceInfo = appointment.appointmentServices.map(
    (appointmentService) => {
      const serviceInfo = serviceAndAddOnServiceInfo(marketServicesForMarket, {
        baseServiceId: appointmentService.baseServiceId,
        addOnServiceIds: appointmentService.addOnServiceIds
      })
      return {
        ...serviceInfo,
        baseServicePrice: appointmentService.price - _.sumBy(serviceInfo.addOnServices, 'price'),
        appointmentService
      }
    }
  )

  // check if we should display the wedding info section
  const weddingServiceNameRegex = /wedding|bridal|flower|bride/i
  const includesWeddingServices = !!appointmentServiceInfo.find(({ name }) =>
    weddingServiceNameRegex.test(name)
  )

  const arrivalWindow = getArrivalWindow(appointment.actualStartDateTime, timezone)
  const endTime = getAppointmentEndTime(appointment, timezone)

  // Promo entry is shared between two placements: the summary breakdown on desktop
  // and, on mobile, between the payment options and gratuity (see the Cro Sprint
  // mobile layout). Only one copy is visible at a time via responsive display.
  const promoCodeFields = (
    <>
      <div className="input-group-btn">
        <Input
          style={{ backgroundColor: 'transparent' }}
          type="text"
          placeholder="Enter a promo code"
          value={promoCode}
          onChange={({ target: { value } }) => setPromoCode(value)}
        />
        <PromoButton disabled={!promoCode} onClick={() => applyPromoCode(promoCode)}>
          Apply
        </PromoButton>
      </div>
      {promoCodeMessage && (
        <p className={`promo-code-message ${promoCodeClass}`}>
          {promoCodeMessage}
        </p>
      )}
    </>
  )

  return (
    <StepFiveLayout>
      <StepTitle align='center' fontSize={48}>
        Review your payment method and appointment details
      </StepTitle>
      <Columns>
      <LeftColumn>
        <PaymentCard
          selectedPaymentMethodFn={selectedPaymentMethodId => {
            dispatch({ selectedPaymentMethodId, bookingError: null })
          }}
          createdPaymentMethodFn={method =>
            dispatch({
              paymentMethods: [...paymentMethods, method],
              selectedPaymentMethodId: method.id,
              bookingError: null
            })
          }
          isDesktop={isDesktop}
          selectedPaymentMethodId={selectedPaymentMethodId}
          paymentMethods={paymentMethods}
          error={bookingError}
          account={false}
          me={me}
          // Only surface the wallet row when platform pay is actually eligible (chargeable
          // total + wallet present); otherwise a stale availability object could show a
          // $0-ineligible Apple/Google Pay entry.
          platformPayAvailability={platformPayEligible ? platformPayAvailability : undefined}
          noPaymentNeeded={noPaymentNeeded}
          market={marketShortName}
          paymentOptionsReady={Number.isFinite(total) && (total <= 0 || !!platformPayAvailability)}
        />

        <MobilePromoCode>
          {promoCodeFields}
        </MobilePromoCode>

        {/* Render the appropriate gratuity component based on market configuration */}
        {adjustableTipRate && (
          <DynamicGratuity
            isTipRateAdjustableMarket={adjustableTipRate}
            onTipRateChange={setTipRate}
            finalPrice={appointment.finalPriceWithTaxAndTip}
            totalPrice={appointment.totalPrice}
          />
        )}

        {fullyAdjustableTipRate && (
          <FullyDynamicGratuity
            isTipRateAdjustableMarket={fullyAdjustableTipRate}
            onTipRateChange={setTipRate}
            onTipInCashChange={setWillTipInCash}
            onCustomInputModeChange={setIsCustomTipInputOpen} // disable "Book Now" while custom input is open
            finalPrice={appointment.finalPriceWithTaxAndTip}
            baseTipAmount={appointment.totalPrice + appointment.handlingFee}
            totalPrice={appointment.totalPrice}
            showTipInCash={showTipInCash}
          />
        )}

        {includesWeddingServices && (
          <WeddingDetailsSection>
            <Header2
              fontSize={36} 
              lineHeight='138%' 
              spacing={0.36}
              align='center'
              margin='0 0 30px'
            >
              Add wedding details.
            </Header2>
            <Label>Wedding Date</Label>
            <CalendarDropdown
              maxWidth={379}
              selected={weddingDate}
              toggle={toggleWeddingCalendar}
              open={weddingCalendarOpen}
              onClick={weddingDate => {
                dispatch({ weddingDetails: { weddingDate }})
                toggleWeddingCalendar()
              }}
            />
            <RegularInput 
              value={weddingLocation}
              placeholder='Wedding Location'
              onChange={({ target: { value: weddingLocation } }) =>
                dispatch({ weddingDetails: { weddingLocation } })
              }
            />
            <Label style={{ marginTop: '30px' }}>Are you interested in other wedding services?</Label>
            <div className="input-group-btn checkbox-list">
              {[
                'Bridal Party Hair',
                'Bridal Party Makeup',
                'Engagement Photos'
              ].map(otherServiceName => (
                <>
                  <div className="gs-checkbox-circle">
                    <input
                      type="checkbox"
                      name={otherServiceName}
                      id={otherServiceName}
                      checked={weddingOtherServices.includes(otherServiceName)}
                      onChange={({ target: { checked } }) => {
                        const updatedOtherServiceNames = checked
                          ? weddingOtherServices.concat([otherServiceName])
                          : weddingOtherServices.filter(
                            name => name !== otherServiceName
                          )

                        return dispatch({
                          weddingDetails: {
                            weddingOtherServices: updatedOtherServiceNames
                          }
                        })
                      }}
                    />
                    <label htmlFor={otherServiceName}>
                      <span className="box"></span>
                      {otherServiceName}
                    </label>
                  </div>
                </>
              ))}
            </div>
          </WeddingDetailsSection>
        )}
      </LeftColumn>
      <SummarySection>
        <SummaryTitle
          fontSize={22}
          lineHeight='120%'
          align='center'
          margin='0 0 30px'
        >
          Appointment summary
        </SummaryTitle>
        <div className='summary-content'>
          <div className='date-time'>
            <img src='/images/calendar-outline.svg' alt='' aria-hidden='true' />
            <div className='summary-detail'>
              <div className='date-time-content'>
                <Text fontSize={18} weight={300} lineHeight='140%'>
                  {formatDateLong(appointment.actualStartDateTime, timezone)}
                </Text>
                <Text fontSize={18} weight={300} lineHeight='140%'>
                  {formatTimeShort(appointment.actualStartDateTime, timezone)}
                </Text>
              </div>
            </div>
          </div>
          <div className='date-time'>
            <img src='/images/location-pin-outline.svg' alt='' aria-hidden='true' />
            <div className='summary-detail'>
              { address?.name && (
                <Text fontSize={18} weight={300} lineHeight='140%'>
                  {address.name}
                </Text>
              )}
              <Text fontSize={18} weight={300} lineHeight='140%'>
                {address.street}
              </Text>
              {
                address.apartment && (
                  <Text
                    fontSize={18}
                    lineHeight="140%"
                    weight={400}
                  >
                    {address.apartment}
                  </Text>
                )
              }
              <Text fontSize={18} weight={300} lineHeight='140%'>
                {address.city}, {address.state} {address.zip}
              </Text>
            </div>
          </div>
          <div className='arrival-window-text' style={{ display: 'flex', width: '100%', flex: 1 }}>
            <Text fontSize={16} weight={400} color={gsColors.darkGrey} style={{ flex: 1, width: '100%' }}>
              Your Beauty Pro{team > 1 ? 's' : ''} will arrive between
              {' '}
              <b>{arrivalWindow[0]} and {arrivalWindow[1]}</b>
              {' '}
              to start the appointment and will finish by
              {' '}
              <b>{endTime}</b>
            </Text>
          </div>
          <div className='summary-divider' />
          <ul className="prices">
            {appointmentServiceInfo.map(({ name, addOnServices, baseServicePrice }, i) => (
              <>
                <li key={i} className="service">
                  <span className="name">{name && name.toLowerCase()}</span>
                  <span>
                    {formatCurrency(baseServicePrice)}
                  </span>
                </li>
                {addOnServices &&
                  addOnServices.map(({ name, price }, j) => (
                    <li key={i * 100 + j} className="service">
                      <span>{name}</span>
                      <span>{formatCurrency(price)}</span>
                    </li>
                  ))}
              </>
            ))}
            <li className="subtotal">
              <span>Subtotal</span>
              <span>{formatCurrency(appointment.totalPrice)}</span>
            </li>
            {appointment.creditsApplied > 0 && (
              <li className="credit">
                <span>Credit</span>
                <span>-{formatCurrency(appointment.creditsApplied)}</span>
              </li>
            )}
            
            {/* if there's tax, split it out from fees */}
            {appointment.taxAmount > 0
              ? (<>
                <li>
                  <span>
                    Tax
                  </span>
                  <span>{formatCurrency(appointment.taxAmount)}</span>
                </li>
                <li>
                  <span>
                    Fees
                  </span>
                  <span>{formatCurrency(appointment.handlingFee)}</span>
                </li>
              </>)
              : (<>
                <li>
                  <span>
                    Tax &amp; Fees
                  </span>
                  <span>{formatCurrency(appointment.taxAmount + appointment.handlingFee)}</span>
                </li>                
              </>)}

            <li>
              <span>
                Gratuity
              </span>
              <span>{formatCurrency(appointment.tipAmount)}</span>
            </li>
            {
              appointment.additionalFees.map((fee, i) => (
                <li key={i}>
                  <span>
                    {fee.description}
                  </span>
                  <span>{formatCurrency(fee.amount)}</span>
                </li>
              ))
            }
            <div className='summary-divider' style={{ margin: '32px 0 12px' }}/>
            <li className="promo-code">
              {promoCodeFields}
            </li>
            <li className="total">
              <span>Total</span>
              <span>{formatCurrency(appointment.finalPriceWithTaxAndTip)}</span>
            </li>
          </ul>
        </div>
        <ProTrustBanner />
        <SummaryPolicies cancellationPolicy={cancellationPolicy} market={marketShortName} />
      </SummarySection>
      </Columns>
    </StepFiveLayout>
  )
}

Five.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired
}

export default Five
